import type { DashboardSnapshot } from '@/data/types'
import type { Category, CategoryTiers, EvolutionTier, MetricKey } from './types'

export interface TierMapping {
  metric: MetricKey
  getValue: (s: DashboardSnapshot) => number
  
  tierThresholds: readonly [number, number, number, number, number]
  
  milestoneStep: number
  
  milestoneMax: number
}

export const TIER_MAPPINGS: Record<Category, TierMapping> = {
  growth: {
    metric: 'clients.active',
    getValue: (s) => s.clients.active,
    tierThresholds: [50, 100, 300, 500, 750],
    milestoneStep: 50,
    milestoneMax: 1500,
  },
  revenue: {
    metric: 'revenue.mrrCzk',
    getValue: (s) => s.revenue.mrrCzk ?? 0,
    tierThresholds: [50_000, 100_000, 300_000, 500_000, 1_000_000],
    milestoneStep: 50_000,
    milestoneMax: 2_000_000,
  },
  reliability: {
    metric: 'reliability.daysWithoutP1',
    getValue: (s) => s.incidents.daysWithoutP1,
    tierThresholds: [7, 14, 30, 60, 90],
    milestoneStep: 7,
    milestoneMax: 180,
  },
  speed: {
    metric: 'incidents.closedTotal',
    getValue: (s) => s.incidents.closedTotal,
    tierThresholds: [100, 500, 2000, 5000, 10000],
    milestoneStep: 100,
    milestoneMax: 15000,
  },
}

export function tierFromValue(value: number, thresholds: readonly number[]): EvolutionTier {
  if (value >= thresholds[4]) return 5
  if (value >= thresholds[3]) return 4
  if (value >= thresholds[2]) return 3
  if (value >= thresholds[1]) return 2
  if (value >= thresholds[0]) return 1
  return 0
}

export function computeEvolution(snapshot: DashboardSnapshot | null | undefined): CategoryTiers {
  const out: CategoryTiers = { growth: 0, reliability: 0, revenue: 0, speed: 0 }
  if (!snapshot) return out
  ;(Object.keys(TIER_MAPPINGS) as Category[]).forEach((cat) => {
    const m = TIER_MAPPINGS[cat]
    out[cat] = tierFromValue(m.getValue(snapshot), m.tierThresholds)
  })
  return out
}

export interface CurrentGoal {
  current: number
  goal: number
  
  progress: number
  
  isMax: boolean
}

export function currentAndNextGoal(
  snapshot: DashboardSnapshot | null | undefined,
  category: Category,
): CurrentGoal {
  const m = TIER_MAPPINGS[category]
  if (!snapshot) return { current: 0, goal: m.milestoneStep, progress: 0, isMax: false }
  const current = m.getValue(snapshot)
  const nextMultiple = Math.ceil((current + 1) / m.milestoneStep) * m.milestoneStep
  if (nextMultiple > m.milestoneMax) {
    return { current, goal: m.milestoneMax, progress: 1, isMax: true }
  }
  const prev = nextMultiple - m.milestoneStep
  const progress = Math.max(0, Math.min(1, (current - prev) / m.milestoneStep))
  return { current, goal: nextMultiple, progress, isMax: false }
}

export interface TierProgress {
  current: number
  prevTierStart: number
  nextTierStart: number
  
  progress: number
  
  isMax: boolean
}

export function progressToNextTier(
  snapshot: DashboardSnapshot | null | undefined,
  category: Category,
): TierProgress {
  const m = TIER_MAPPINGS[category]
  if (!snapshot) {
    return {
      current: 0,
      prevTierStart: 0,
      nextTierStart: m.tierThresholds[0],
      progress: 0,
      isMax: false,
    }
  }
  const current = m.getValue(snapshot)
  const tier = tierFromValue(current, m.tierThresholds)
  if (tier >= 5) {
    return {
      current,
      prevTierStart: m.tierThresholds[4],
      nextTierStart: m.tierThresholds[4],
      progress: 1,
      isMax: true,
    }
  }
  const idx = tier as 0 | 1 | 2 | 3 | 4
  const prevTierStart: number = idx === 0 ? 0 : m.tierThresholds[idx - 1]
  const nextTierStart: number = m.tierThresholds[idx]
  const span = Math.max(1, nextTierStart - prevTierStart)
  const progress = Math.max(0, Math.min(1, (current - prevTierStart) / span))
  return { current, prevTierStart, nextTierStart, progress, isMax: false }
}
